import { Checkbox } from 'antd'
import type { TableProps } from 'antd'
import { memo, useCallback, useContext, useSyncExternalStore } from 'react'
import type { CSSProperties, Key, MouseEvent, ReactNode } from 'react'

import {
  columnWidthVar,
  JUSTIFY_BY_ALIGN,
  readCellValue
} from './treadmillMath'
import type { ColumnLayout, ColumnWindow, TreadmillLeaf } from './treadmillMath'
import { TreadmillSelectionContext } from './treadmillSelection'
import { useTreadmillStyles } from './treadmillStyles'

const stopPropagation = (event: MouseEvent) => event.stopPropagation()

const noopSubscribe = () => () => undefined

type SelectionCellProps<T> = {
  record: T
  rowKeyValue: Key
  width: number
}

const TreadmillSelectionCell = <T,>({
  record,
  rowKeyValue,
  width
}: SelectionCellProps<T>) => {
  const { styles, cx } = useTreadmillStyles()
  const selection = useContext(TreadmillSelectionContext)
  const subscribe = selection?.subscribe ?? noopSubscribe
  const isSelected = useSyncExternalStore(
    subscribe,
    useCallback(
      () => Boolean(selection?.isSelected(rowKeyValue)),
      [selection, rowKeyValue]
    )
  )

  if (!selection) {
    return null
  }

  return (
    <div
      className={cx(styles.treadmillCell, styles.treadmillSelectionCell)}
      style={{ width }}
      onClick={stopPropagation}
    >
      <Checkbox
        checked={isSelected}
        disabled={selection.isDisabled(record)}
        onChange={(event) =>
          selection.toggle(rowKeyValue, event.target.checked)
        }
      />
    </div>
  )
}

type TreadmillRowProps<T> = {
  record: T
  realIndex: number
  rowKeyValue: Key
  layout: ColumnLayout<T>
  columnWindow?: ColumnWindow
  placeholder?: boolean
  rowTop: number
  rowHeight: number
  selectionWidth: number
  hasSelection: boolean
  rowClassName?: string
  onRow?: TableProps<T>['onRow']
}

const TreadmillRowComponent = <T,>({
  record,
  realIndex,
  rowKeyValue,
  layout,
  columnWindow,
  placeholder = false,
  rowTop,
  rowHeight,
  selectionWidth,
  hasSelection,
  rowClassName,
  onRow
}: TreadmillRowProps<T>) => {
  const { styles, cx } = useTreadmillStyles()
  const rowProps = onRow?.(record, realIndex)

  const renderLeafCell = (
    leaf: TreadmillLeaf<T>,
    extraClassName?: string,
    extraStyle?: CSSProperties
  ): ReactNode => {
    const key = leaf.key ?? String(leaf.dataIndex)
    const widthStyle = {
      width: `var(${columnWidthVar(leaf.key)}, ${leaf.width}px)`,
      textAlign: leaf.align,
      justifyContent: leaf.align && JUSTIFY_BY_ALIGN[leaf.align]
    }

    if (placeholder) {
      return (
        <div
          key={key}
          className={cx(styles.treadmillCell, extraClassName)}
          style={{ ...widthStyle, ...extraStyle }}
        >
          <span className={styles.placeholderBar} />
        </div>
      )
    }

    const value = readCellValue(record, leaf.dataIndex)
    const content = leaf.render
      ? (leaf.render(value, record, realIndex) as ReactNode)
      : ((value ?? null) as ReactNode)
    const {
      colSpan: _colSpan,
      rowSpan: _rowSpan,
      className: cellClassName,
      style: cellStyle,
      ...cellProps
    } = leaf.onCell?.(record, realIndex) ?? {}

    return (
      <div
        {...cellProps}
        key={key}
        className={cx(styles.treadmillCell, extraClassName, cellClassName)}
        style={{ ...widthStyle, ...extraStyle, ...cellStyle }}
        onClick={
          cellProps.onClick ?? (extraClassName ? stopPropagation : undefined)
        }
      >
        {content}
      </div>
    )
  }

  return (
    <div
      {...rowProps}
      data-row-key={String(rowKeyValue)}
      className={cx(
        styles.treadmillRow,
        rowProps?.onClick && styles.clickableRow,
        rowClassName
      )}
      style={{ height: rowHeight, transform: `translateY(${rowTop}px)` }}
    >
      {hasSelection && (
        <TreadmillSelectionCell<T>
          record={record}
          rowKeyValue={rowKeyValue}
          width={selectionWidth}
        />
      )}

      {layout.head.map((leaf, index) =>
        renderLeafCell(
          leaf,
          cx(
            styles.treadmillLeftCell,
            index === layout.head.length - 1 && styles.treadmillLeftEdge
          ),
          { left: layout.headLefts[index] }
        )
      )}

      {columnWindow ? (
        <>
          <div
            className={styles.treadmillSpacer}
            style={{ width: columnWindow.before }}
          />
          {layout.body
            .slice(columnWindow.first, columnWindow.last + 1)
            .map((leaf) => renderLeafCell(leaf))}
          <div
            className={styles.treadmillSpacer}
            style={{ width: columnWindow.after }}
          />
        </>
      ) : (
        layout.body.map((leaf) => renderLeafCell(leaf))
      )}

      {layout.tail.map((leaf, index) =>
        renderLeafCell(
          leaf,
          cx(
            styles.treadmillRightCell,
            index === 0 && styles.treadmillRightEdge
          ),
          { right: layout.tailOffsets[index] }
        )
      )}
    </div>
  )
}

export const TreadmillRow = memo(
  TreadmillRowComponent
) as typeof TreadmillRowComponent
